import { observer } from 'mobx-react';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import type { ISupportNotification } from '../../../models/Support/ISupportNotification';
import { useSupportMailboxListQuery } from '../../../queries/Support/useSupportMailboxQuery';
import {
	useFetchLatestSupportNotifications,
	useMarkNotificationReadMutation,
	useMarkNotificationsReadMutation,
	useSupportNotificationsQuery,
	useSupportNotificationsUnreadCountQuery,
} from '../../../queries/Support/useSupportNotifications';
import { useSupportNotificationsRealtime } from '../../../queries/Support/useSupportNotificationsRealtime';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';
import { type SupportNotificationDestination, SupportNotificationsBellView } from './SupportNotificationsBellView';
import { useDesktopNotificationPermission, useSupportDesktopNotifications } from './useSupportDesktopNotifications';

const FEED_PAGE_SIZE = 20;
// Shared with the bell's feed query so a popup's fetch also fills the dropdown's cache.
const FEED_FIRST_PAGE = { page: 1, pageSize: FEED_PAGE_SIZE } as const;

function safeDecode(value: string) {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}

function getSupportPathFromClickUrl(clickUrl?: string) {
	if (!clickUrl) return null;

	try {
		const baseUrl = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
		const parsed = new URL(clickUrl, baseUrl);
		const hashPath = parsed.hash.replace(/^#/, '');
		const path = hashPath || `${parsed.pathname}${parsed.search}`;
		return path.startsWith('/support') ? path : null;
	} catch {
		return null;
	}
}

function normalizeSupportPath(path: string) {
	const [pathname, queryString = ''] = path.split('?');
	const queryParams = new URLSearchParams(queryString);
	const legacyTicketPathMatch = pathname.match(/^\/support\/ticket\/([^/?#]+)/);

	if (legacyTicketPathMatch) {
		queryParams.set('ticketId', safeDecode(legacyTicketPathMatch[1]));
		queryParams.set('view', 'desk');
		return `/support?${queryParams.toString()}`;
	}

	if (pathname === '/support' && queryParams.has('ticketId') && !queryParams.has('view')) {
		queryParams.set('view', 'desk');
		return `/support?${queryParams.toString()}`;
	}

	return path;
}

function getTicketSupportPath(ticketId: string, threadId?: string) {
	const queryParams = new URLSearchParams({ view: 'desk', ticketId });
	if (threadId) queryParams.set('threadId', threadId);
	return `/support?${queryParams.toString()}`;
}

function getNotificationSupportPath(notification: ISupportNotification) {
	if (notification.ticketId) {
		return getTicketSupportPath(notification.ticketId, notification.threadId);
	}

	const clickUrlSupportPath = getSupportPathFromClickUrl(notification.clickUrl);
	return clickUrlSupportPath ? normalizeSupportPath(clickUrlSupportPath) : null;
}

function getNotificationDestination(notification: ISupportNotification): SupportNotificationDestination | null {
	const supportPath = getNotificationSupportPath(notification);
	if (supportPath) return { kind: 'route', path: supportPath };
	if (notification.clickUrl) return { kind: 'external', url: notification.clickUrl };
	return null;
}

export const SupportNotificationsBell = observer(() => {
	const userSession = useAdminUserSession();
	const hasSupportAccess = userSession.canAccessSupport || userSession.hasPermission(AdminPermission.OnlyForDevelopers);
	const { data: mailboxes } = useSupportMailboxListQuery({ enabled: hasSupportAccess });

	if (!hasSupportAccess) return null;
	if (!mailboxes?.length) return null;
	return <SupportNotificationsBellContent />;
});

const SupportNotificationsBellContent: React.FC = () => {
	const history = useHistory();
	const [open, setOpen] = React.useState(false);

	const { data: unreadCount } = useSupportNotificationsUnreadCountQuery();
	const feedQuery = useSupportNotificationsQuery(FEED_FIRST_PAGE, { enabled: open });
	const loadLatestNotifications = useFetchLatestSupportNotifications(FEED_FIRST_PAGE);
	const markRead = useMarkNotificationReadMutation();
	const markBatchRead = useMarkNotificationsReadMutation();
	const desktopNotifications = useDesktopNotificationPermission();
	useSupportNotificationsRealtime();

	const notifications = feedQuery.data?.values ?? [];
	const totalCount = feedQuery.data?.totalCount ?? 0;

	// Marking read is best-effort; settled invalidation restores the true unread state.
	const markNotificationRead = (notification: ISupportNotification) => {
		if (!notification.readDate) markRead.mutate(notification.id);
	};

	// The notification's link does the navigating. A new-tab open keeps the panel up so the agent can open several.
	const openNotification = (notification: ISupportNotification, { opensInNewTab }: { opensInNewTab: boolean }) => {
		markNotificationRead(notification);
		if (!opensInNewTab) setOpen(false);
	};

	// A desktop popup has no link to follow, so it navigates here.
	const openDesktopNotification = (notification: ISupportNotification) => {
		markNotificationRead(notification);
		setOpen(false);
		const destination = getNotificationDestination(notification);
		if (destination?.kind === 'route') history.push(destination.path);
		else if (destination?.kind === 'external') window.location.assign(destination.url);
	};

	useSupportDesktopNotifications({
		unreadCount,
		loadLatestNotifications,
		onNotificationClick: openDesktopNotification,
		onSummaryClick: () => setOpen(true),
	});

	return (
		<SupportNotificationsBellView
			isOpen={open}
			onOpenChange={setOpen}
			notifications={notifications}
			unreadCount={unreadCount ?? 0}
			totalCount={totalCount}
			isLoading={feedQuery.isLoading}
			isError={feedQuery.isError}
			isMarkAllReadPending={markBatchRead.isPending}
			getNotificationDestination={getNotificationDestination}
			onNotificationClick={openNotification}
			onMarkAllRead={() => markBatchRead.mutate(undefined)}
			desktopNotificationPermission={desktopNotifications.permission}
			onEnableDesktopNotifications={desktopNotifications.requestPermission}
		/>
	);
};
